import { useEffect, useState } from "react";
import { InterviewAssessment, PersonaProfile } from "@type/assessments";
import { Button } from "@component/button";
import { ArrowLeft } from "lucide-react";
import { DailyProvider, useCallObject } from "@daily-co/daily-react";
import { SpeechDetectionProvider } from "@context/speech_detection_context";
import { DailyCall } from "@daily-co/daily-js";
import { CustomInterviewSection } from "@type/videoInterview";
import AuthLayout from "@container/layout/auth";
import InterviewFormV2 from "@container/asess-section/interview-form-v2";
import CustomVoiceInterviewV2 from "@container/custom-voice-interview-v2";
import ELVoiceInterview from "@container/el-voice-interview";
import { useUserProfileContext } from "@context/user_profile";
import { getCompanyInfo } from "@service/user";
import { Company } from "@type/company";

export default function EmployerAssessInterviewCreatePage() {
    const callObject = useCallObject({}) as DailyCall;
    const [isLoadingUserProfile, userInfo] = useUserProfileContext();
    const [companyInfo, setCompanyInfo] = useState<Company | undefined>(undefined);
    // Whether the company-info fallback fetch has settled (resolved OR failed).
    const [companyFetchSettled, setCompanyFetchSettled] = useState<boolean>(false);
    // Whether that fallback fetch FAILED — when the profile also lacks
    // interview_engine, the engine is genuinely unknown and we must NOT default to
    // v1 (an ElevenLabs company would test on the wrong engine).
    const [companyFetchFailed, setCompanyFetchFailed] = useState<boolean>(false);
    const [selectedPersona, setSelectedPersona] = useState<PersonaProfile | null>(null);
    const [testInterviewConfig, setTestInterviewConfig] = useState<InterviewAssessment | undefined>(undefined);
    // Synthetic test user id — minted once per started test so it stays stable for
    // the session (ELVoiceInterview uses it for status/feedback correlation).
    const [testUserId, setTestUserId] = useState<string | null>(null);

    // The interview engine is a company-level flag. Prefer the synchronous value
    // from /auth/me (no fetch, no race) and only fall back to a company-info fetch
    // if /auth/me didn't carry it — so the test interview deterministically uses
    // the same engine the candidate will (v2 ElevenLabs vs the v1 pipecat flow).
    const interviewEngine = userInfo?.interview_engine ?? companyInfo?.interview_engine ?? null;
    // The engine stays "unresolved" while the profile loads, OR while the
    // company-info fallback is still in flight (profile lacked interview_engine but
    // carries a company_id). Gating render on this stops HR from briefly mounting
    // the v1 engine before the fallback resolves and flips it to v2. We key off
    // whether the fetch has SETTLED (resolved or failed) — not just whether
    // companyInfo is set — so a failed fetch falls through to v1 instead of hanging
    // on a blank screen.
    const isResolvingInterviewEngine =
        isLoadingUserProfile ||
        (!userInfo?.interview_engine && !!userInfo?.company_id && !companyFetchSettled);
    // The engine can't be determined: profile lacks it AND the fallback fetch failed.
    const engineUnresolvable =
        !isResolvingInterviewEngine && !userInfo?.interview_engine && companyFetchFailed;

    useEffect(() => {
        if (!isLoadingUserProfile && !userInfo?.interview_engine && userInfo?.company_id) {
            getCompanyInfo(userInfo.company_id)
                .then((resp) => {
                    if (resp.status === 200) setCompanyInfo(resp.data.data);
                    else setCompanyFetchFailed(true);
                })
                .catch(() => setCompanyFetchFailed(true))
                .finally(() => setCompanyFetchSettled(true));
        }
    }, [isLoadingUserProfile, userInfo?.company_id, userInfo?.interview_engine]);

    const onStartTestInterview = (persona: PersonaProfile, interviewData: InterviewAssessment) => {
        setSelectedPersona(persona);
        setTestInterviewConfig(interviewData);
        setTestUserId(`test-${Date.now()}`);
    }

    const handleBackFromTest = () => {
        setSelectedPersona(null);
        setTestInterviewConfig(undefined);
        setTestUserId(null);
    }

    return (
        <AuthLayout>
            {(selectedPersona && testInterviewConfig) && (
                <div className="padding-4 h-full flex flex-col">
                    {/* Back button header */}
                    <div className="bg-white p-4 sticky top-0 z-10">
                        <div className="max-w-6xl mx-auto w-full">
                            <div className="flex justify-between items-center">
                                <Button
                                    variant="outline"
                                    onClick={handleBackFromTest}
                                    className="flex items-center gap-2"
                                >
                                    <ArrowLeft className="h-4 w-4" />
                                    Back to Editor
                                </Button>
                            </div>
                        </div>
                    </div>

                    {/* Full-screen test interview */}
                    <div className="flex-1 overflow-y-auto ">
                        <div className="max-w-6xl mx-auto w-full px-4">
                            {/* Engine routing (#2810): wait for the profile so the
                                engine is picked deterministically, then v2 ElevenLabs
                                companies test with <ELVoiceInterview>; v1/null keep
                                the pipecat flow. */}
                            {isResolvingInterviewEngine ? null : engineUnresolvable ? (
                                <div className="w-full py-10 flex flex-col items-center justify-center gap-4 text-center">
                                    <p className="text-sm text-red-600 max-w-md">
                                        We couldn&apos;t load the interview configuration. Please refresh and try again.
                                    </p>
                                    <Button onClick={() => window.location.reload()}>Reload</Button>
                                </div>
                            ) : interviewEngine === "elevenlabs" ? (
                                <ELVoiceInterview
                                    isTestMode
                                    assessmentId={testInterviewConfig?.id || ""}
                                    userId={testUserId || "test-user"}
                                    candidateName={selectedPersona?.name || "Test User"}
                                    interviewName={testInterviewConfig?.content.interviewName || "Test Interview"}
                                    description={testInterviewConfig?.content.description}
                                    aiName={testInterviewConfig?.content.aiName}
                                    language={testInterviewConfig?.content.interviewLanguage || "en-US"}
                                    voice={testInterviewConfig?.content.voice || "male"}
                                    sections={(testInterviewConfig?.content.sections || []).map((s) => ({ title: s.title, duration: s.duration }))}
                                    maxDuration={testInterviewConfig?.content.maxDuration || 0}
                                    onCompletedInterview={handleBackFromTest}
                                    onCloseModal={handleBackFromTest}
                                />
                            ) : (
                            <DailyProvider callObject={callObject}>
                                <SpeechDetectionProvider
                                    volumeThreshold={30}
                                    silenceDelay={400}
                                    autoStart={true}
                                >
                                    <CustomVoiceInterviewV2
                                        isPracticeInterview
                                        isTestInterview={true}
                                        interviewId={"abc"}
                                        pipecatApiUrl={import.meta.env.VITE_PIPECAT_API_URL || ""}
                                        agentName={import.meta.env.VITE_PIPECAT_CUSTOM_AGENT_NAME || ""}
                                        authToken={import.meta.env.VITE_PIPECAT_AUTH_TOKEN || ""}
                                        s3BucketName={import.meta.env.VITE_S3_BUCKET_NAME || ""}
                                        s3BucketRegion={import.meta.env.VITE_S3_BUCKET_REGION || ""}
                                        awsAssumeRoleArn={import.meta.env.VITE_AWS_ASSUME_ROLE_ARN || ""}
                                        fastapiURL={import.meta.env.VITE_TALENT_AI_BE_BASE_URL || ""}
                                        userId={`test-${Date.now()}`}
                                        userName={selectedPersona?.name || "Test User"}
                                        interviewName={testInterviewConfig?.content.interviewName || "Test Interview"}
                                        description={testInterviewConfig?.content.description || ""}
                                        maxDuration={testInterviewConfig?.content.maxDuration || 0}
                                        interviewContext={testInterviewConfig?.content.interviewContext || "You are conducting a test interview."}
                                        closingStatement={testInterviewConfig.content.closingStatement || ""}
                                        interviewerPersona={testInterviewConfig?.content.interviewerPersona || ""}
                                        toneAndCommunication={testInterviewConfig?.content.toneAndCommunication || undefined}
                                        interviewFlowPrinciples={testInterviewConfig?.content.interviewFlowPrinciples || undefined}
                                        informationToShare={testInterviewConfig?.content.informationToShare || undefined}
                                        customGreeting={testInterviewConfig?.content.customGreeting || ""}
                                        aiName={testInterviewConfig?.content.aiName || ""}
                                        sections={testInterviewConfig?.content.sections as CustomInterviewSection[]}
                                        onCloseModal={handleBackFromTest}
                                        onCompletedInterview={handleBackFromTest}
                                        interviewLanguage={testInterviewConfig?.content.interviewLanguage || ""}
                                        voice={testInterviewConfig?.content.voice || "male"}
                                        idleTimeoutMessage={testInterviewConfig?.content.idleTimeoutMessage || ""}
                                    />
                                </SpeechDetectionProvider>
                            </DailyProvider>
                            )}
                        </div>
                    </div>
                </div>
            )}
            <div className={(selectedPersona) ? "hidden" : ""}>
                <InterviewFormV2 onStartTestInterview={onStartTestInterview} />
            </div>
        </AuthLayout>
    );
}
